import { useEffect, useMemo, useState } from 'react'

/* ============================================================
   THEMED DATA GRID
   react-data-table-component styles itself through a JS object,
   so it cannot read CSS custom properties on its own. We read
   the tokens off :root and re-read them whenever the theme
   attribute or the OS colour-scheme changes, which is what makes
   the grid repaint with the rest of the product.

   Written for the challenges cluster, but nothing in it is
   challenge-specific — any admin screen that renders a
   <DataTable> should use it:

     import { useTableStyles } from './tableTheme'
     const tableStyles = useTableStyles()
     <DataTable customStyles={tableStyles} ... />

   No colour is ever spelled out here: every value is a token
   from theme/tokens.css.
   ============================================================ */

const TOKEN_NAMES = [
  '--panel', '--panel-raise', '--line', '--line-soft',
  '--ink', '--ink-2', '--ink-3', '--accent', '--accent-wash',
  '--mono', '--sans', '--step-0'
]

function readTokens() {
  const out = {}
  if (typeof window === 'undefined' || !document.documentElement) return out
  const styles = window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach((name) => {
    out[name] = String(styles.getPropertyValue(name) || '').trim()
  })
  return out
}

export function useThemeTokens() {
  const [tokens, setTokens] = useState(readTokens)

  useEffect(() => {
    let frame = null

    const refresh = () => {
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame = window.requestAnimationFrame(() => setTokens(readTokens()))
    }

    const observer = new MutationObserver(refresh)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style']
    })

    let media = null
    if (window.matchMedia) {
      media = window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change', refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media) {
        if (media.removeEventListener) media.removeEventListener('change', refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  }, [])

  return tokens
}

export function useTableStyles() {
  const tokens = useThemeTokens()

  return useMemo(() => {
    const t = tokens
    return {
      table: { style: { backgroundColor: t['--panel'], color: t['--ink'] } },
      tableWrapper: { style: { display: 'block' } },
      responsiveWrapper: { style: { overflow: 'visible' } },
      head: { style: { color: t['--ink-3'] } },
      headRow: {
        style: {
          backgroundColor: t['--panel-raise'],
          color: t['--ink-3'],
          minHeight: '42px',
          borderBottomWidth: '1px',
          borderBottomColor: t['--line'],
          borderBottomStyle: 'solid'
        }
      },
      headCells: {
        style: {
          paddingLeft: '12px',
          paddingRight: '12px',
          fontFamily: t['--mono'],
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: t['--ink-3']
        }
      },
      rows: {
        style: {
          backgroundColor: t['--panel'],
          color: t['--ink'],
          fontFamily: t['--sans'],
          fontSize: t['--step-0'] || '0.875rem',
          minHeight: '52px',
          '&:not(:last-of-type)': {
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: t['--line-soft']
          }
        },
        highlightOnHoverStyle: {
          backgroundColor: t['--accent-wash'],
          color: t['--ink'],
          borderBottomColor: t['--line-soft'],
          outlineWidth: '0px',
          outlineColor: t['--panel'],
          transitionDuration: '0.15s',
          transitionProperty: 'background-color'
        }
      },
      cells: { style: { paddingLeft: '12px', paddingRight: '12px' } },
      pagination: {
        style: {
          backgroundColor: t['--panel'],
          color: t['--ink-3'],
          fontSize: '12px',
          minHeight: '50px',
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderTopColor: t['--line']
        },
        pageButtonsStyle: {
          color: t['--ink-2'],
          fill: t['--ink-2'],
          backgroundColor: 'transparent',
          '&:disabled': { color: t['--ink-3'], fill: t['--ink-3'] },
          '&:hover:not(:disabled)': { backgroundColor: t['--panel-raise'] },
          '&:focus': { outline: 'none', backgroundColor: t['--accent-wash'] }
        }
      },
      noData: { style: { backgroundColor: t['--panel'], color: t['--ink-3'], padding: '26px' } },
      progress: { style: { backgroundColor: t['--panel'], color: t['--ink-3'] } }
    }
  }, [tokens])
}

export default useTableStyles
